import React, { useEffect, useRef, useState } from 'react';
import { ChevronUp, Skull, Sparkles, X, Zap } from 'lucide-react';
import { soundEngine } from '../audio/soundEngine.ts';

interface HexLifeBadgeProps {
  seatId: string;
  seatName: string;
  life: number;
  profileColor: string;
  playerCounters: Record<string, number>;
  canEdit: boolean;
  onCommitLifeDelta: (delta: number) => void;
  onAdjustCounter: (counter: string, delta: number) => void;
}

const PRESET_PLAYER_COUNTERS = [
  { key: 'poison', label: 'Poison' },
  { key: 'energy', label: 'Energy' },
  { key: 'experience', label: 'Experience' },
  { key: 'rad', label: 'Radiation' },
  { key: 'storm', label: 'Storm' },
  { key: 'speed', label: 'Speed' },
  { key: 'ticket', label: 'Ticket' },
  { key: 'ring', label: 'Ring Tempts' },
];

export function HexLifeBadge({
  seatId,
  seatName,
  life,
  profileColor,
  playerCounters,
  canEdit,
  onCommitLifeDelta,
  onAdjustCounter,
}: HexLifeBadgeProps) {
  const [pendingDelta, setPendingDelta] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [customName, setCustomName] = useState('');
  const badgeContainerRef = useRef<HTMLDivElement | null>(null);
  const pendingRef = useRef(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!drawerOpen) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (badgeContainerRef.current && !badgeContainerRef.current.contains(e.target as Node)) {
        setDrawerOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [drawerOpen]);

  const flushPending = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (pendingRef.current !== 0) {
      const toSend = pendingRef.current;
      pendingRef.current = 0;
      setPendingDelta(0);
      onCommitLifeDelta(toSend);
    }
  };

  useEffect(() => {
    return () => {
      if (holdIntervalRef.current) clearInterval(holdIntervalRef.current);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const stepLife = (step: number) => {
    if (!canEdit) return;
    soundEngine.play('lifeTick');
    pendingRef.current += step;
    setPendingDelta(pendingRef.current);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(flushPending, 550);
  };

  const startHold = (step: number) => {
    stepLife(step);
    if (holdIntervalRef.current) clearInterval(holdIntervalRef.current);
    holdIntervalRef.current = setInterval(() => stepLife(step), 145);
  };

  const stopHold = () => {
    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current);
      holdIntervalRef.current = null;
    }
  };

  const displayedLife = life + pendingDelta;
  const poison = playerCounters.poison ?? 0;
  const lethalPoison = poison >= 10;
  const activeCounters = Object.entries(playerCounters).filter(([, v]) => v > 0);

  return (
    <div
      ref={badgeContainerRef}
      data-zone-anchor={`${seatId}:life`}
      aria-label={`${seatName} life total ${displayedLife}`}
      className={`relative flex flex-col items-center gap-1 select-none ${drawerOpen ? 'z-[85]' : 'z-10'}`}
    >
      <div className="flex items-center gap-1">
        {canEdit && (
          <button
            type="button"
            aria-label="Decrease life"
            onPointerDown={() => startHold(-1)}
            onPointerUp={stopHold}
            onPointerLeave={stopHold}
            onPointerCancel={stopHold}
            className="w-6 h-7 rounded-md bg-slate-900/95 border border-slate-700/90 hover:border-rose-400/70 text-rose-300 font-bold text-sm flex items-center justify-center active:scale-95"
          >
            −
          </button>
        )}

        {/* Ultra PRO Hexagonal Life Frame */}
        <div className="relative w-14 h-14 flex flex-col items-center justify-center">
          <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full drop-shadow-md">
            <polygon
              points="50,4 93,27 93,73 50,96 7,73 7,27"
              fill="#0a0f1d"
              stroke={displayedLife <= 0 ? '#f43f5e' : profileColor}
              strokeWidth="3.5"
            />
          </svg>
          <span
            className={`relative z-10 font-black text-lg leading-none tracking-tight ${
              displayedLife <= 0 ? 'text-rose-400' : 'text-white'
            }`}
          >
            {displayedLife}
          </span>
          <span className="relative z-10 text-[7px] uppercase tracking-widest text-slate-400 mt-0.5">
            Life
          </span>

          {pendingDelta !== 0 && (
            <span
              className={`absolute -top-2 -right-2 z-20 px-1.5 py-0.5 rounded-full text-[10px] font-extrabold shadow ${
                pendingDelta > 0 ? 'bg-emerald-500 text-slate-950' : 'bg-rose-500 text-white'
              }`}
            >
              {pendingDelta > 0 ? `+${pendingDelta}` : pendingDelta}
            </span>
          )}
        </div>

        {canEdit && (
          <button
            type="button"
            aria-label="Increase life"
            onPointerDown={() => startHold(1)}
            onPointerUp={stopHold}
            onPointerLeave={stopHold}
            onPointerCancel={stopHold}
            className="w-6 h-7 rounded-md bg-slate-900/95 border border-slate-700/90 hover:border-emerald-400/70 text-emerald-300 font-bold text-sm flex items-center justify-center active:scale-95"
          >
            +
          </button>
        )}
      </div>

      {/* Active Player Counters Summary + Popover Trigger */}
      <div className="flex items-center gap-1 flex-wrap justify-center max-w-[112px]">
        {activeCounters.map(([k, v]) => (
          <span
            key={k}
            className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold flex items-center gap-0.5 border ${
              k === 'poison' && lethalPoison
                ? 'bg-emerald-950 border-emerald-400 text-emerald-200 ring-1 ring-emerald-400'
                : 'bg-slate-900/90 border-slate-700 text-slate-200'
            }`}
          >
            {k === 'poison' ? <Skull className="w-2.5 h-2.5 text-emerald-400" /> : <Zap className="w-2.5 h-2.5 text-amber-400" />}
            <span>
              {k.slice(0, 3).toUpperCase()} {v}
            </span>
          </span>
        ))}

        {canEdit && (
          <button
            type="button"
            onClick={() => setDrawerOpen((o) => !o)}
            className="px-2 py-0.5 rounded-full bg-emerald-950/60 hover:bg-emerald-900/70 border border-emerald-600/50 text-[9px] font-bold text-emerald-300 flex items-center gap-0.5"
          >
            <ChevronUp className="w-2.5 h-2.5" />
            <span>Counters</span>
          </button>
        )}
      </div>

      {/* Expandable Counters Popover (Poison, Energy, Experience, Rad, Storm, Speed, Ring, Custom) */}
      {canEdit && drawerOpen && (
        <div
          style={{ backgroundColor: '#0b101d' }}
          className="absolute left-0 top-full mt-1.5 z-[95] w-60 rounded-2xl bg-[#0b101d] border border-slate-600 shadow-[0_24px_60px_rgba(0,0,0,0.95)] p-3 text-xs space-y-2.5"
        >
          <div className="flex items-center justify-between border-b border-slate-800 pb-1.5">
            <span className="font-bold text-slate-100">Counters · {seatName}</span>
            <button type="button" onClick={() => setDrawerOpen(false)} className="text-slate-400 hover:text-white">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="space-y-1.5">
            {PRESET_PLAYER_COUNTERS.map((item) => {
              const val = playerCounters[item.key] ?? 0;
              return (
                <div key={item.key} className="flex items-center justify-between text-[11px]">
                  <span className="text-slate-300">{item.label}</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onAdjustCounter(item.key, -1)}
                      className="w-5 h-5 rounded bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-300"
                    >
                      −
                    </button>
                    <span className={`w-5 text-center font-bold ${val > 0 ? 'text-emerald-300' : 'text-slate-500'}`}>
                      {val}
                    </span>
                    <button
                      type="button"
                      onClick={() => onAdjustCounter(item.key, 1)}
                      className="w-5 h-5 rounded bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-100"
                    >
                      +
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex gap-1.5 pt-1 border-t border-slate-800">
            <input
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder="Custom counter..."
              className="flex-1 px-2 py-1 rounded bg-slate-900 border border-slate-700 text-[11px]"
            />
            <button
              type="button"
              onClick={() => {
                if (customName.trim()) {
                  onAdjustCounter(customName.trim().toLowerCase(), 1);
                  setCustomName('');
                }
              }}
              className="px-2.5 py-1 rounded bg-emerald-600 hover:bg-emerald-500 text-slate-950 font-bold text-[10px]"
            >
              Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
